import React, { useEffect, useRef, useState, JSX } from "react";
import Quill from "quill";
import cx from "classnames";
import { dataTestIds } from "data-testids";
import { WarningIcon } from "assets";
import { InputLabel } from "../../Atoms/InputsCommon/InputLabel";
import "quill/dist/quill.snow.css";
import { formats, modules } from "./richTextConfig";
import {
  getEditorContent,
  enforceMaxLength,
  setQuillRef,
} from "./helpers/helpers";

import "./style.scss";

// Prepend http:// to links if scheme is missing
const Link = Quill.import("formats/link");
Link.sanitize = function (url: string) {
  // quill by default creates relative links if scheme is missing.
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return `https://${url}`;
  }

  return url;
};

export interface IRichTextAreaProps {
  label: string;
  formikValue: string;
  formikOnChange: (value: string) => void;
  placeholder?: string;
  whiteInput?: boolean;
  isInvalid?: boolean;
  validationErrorMessage?: string;
  annex?: JSX.Element;
  maxLength?: number;
  textAreaRef?: React.Ref<Quill>;
  customClassNameForQuill?: string;
  customClassNameForCounter?: string;
  "data-testid"?: string;
}

const RichTextArea = ({
  maxLength,
  label,
  placeholder,
  formikValue,
  formikOnChange,
  isInvalid,
  validationErrorMessage = "Required",
  annex,
  textAreaRef,
  customClassNameForQuill,
  customClassNameForCounter,
  whiteInput,
  "data-testid": dataTestId,
}: IRichTextAreaProps) => {
  const [focused, setFocused] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const editorHostRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const formikOnChangeRef = useRef(formikOnChange);
  const formikValueRef = useRef(formikValue);
  const maxLengthRef = useRef(maxLength);

  formikOnChangeRef.current = formikOnChange;
  formikValueRef.current = formikValue;
  maxLengthRef.current = maxLength;

  useEffect(() => {
    const editorHost = editorHostRef.current;

    if (!editorHost) return;

    const quillInstance = new Quill(editorHost, {
      bounds: ".app",
      formats,
      modules,
      placeholder,
      theme: "snow",
    });

    quillInstance.root.tabIndex = 0;
    quillInstance.clipboard.dangerouslyPasteHTML(
      formikValueRef.current || "",
      "silent"
    );
    quillRef.current = quillInstance;
    setCharCount(
      enforceMaxLength(quillInstance, maxLengthRef.current).charCount
    );
    setQuillRef(textAreaRef, quillInstance);

    const handleTextChange = () => {
      const editorState = enforceMaxLength(quillInstance, maxLengthRef.current);

      setCharCount(editorState.charCount);
      formikOnChangeRef.current(getEditorContent(quillInstance));
    };
    const handleFocus = () => setFocused(true);
    const handleBlur = () => setFocused(false);

    quillInstance.on("text-change", handleTextChange);
    quillInstance.root.addEventListener("focus", handleFocus);
    quillInstance.root.addEventListener("blur", handleBlur);

    return () => {
      quillInstance.off("text-change", handleTextChange);
      quillInstance.root.removeEventListener("focus", handleFocus);
      quillInstance.root.removeEventListener("blur", handleBlur);

      setQuillRef(textAreaRef, null);
      quillRef.current = null;
      editorHost.replaceChildren();
    };
  }, [placeholder, textAreaRef]);

  useEffect(() => {
    const quillInstance = quillRef.current;

    if (!quillInstance) return;

    if (getEditorContent(quillInstance) !== formikValue) {
      quillInstance.clipboard.dangerouslyPasteHTML(formikValue || "", "silent");
    }

    const editorState = enforceMaxLength(quillInstance, maxLength);

    setCharCount(editorState.charCount);

    if (editorState.wasTrimmed) {
      formikOnChangeRef.current(getEditorContent(quillInstance));
    }
  }, [formikValue, maxLength]);

  const wrapperClassNames = cx("rich-text__wrapper", {
    "rich-text__wrapper--invalid": isInvalid,
    "rich-text__wrapper--white": !!formikValue || focused || whiteInput,
  });

  const getValidationError = () => {
    return (
      <div className="flex items-center justify-start gap-1 pt-[2px] text-sm font-semibold">
        <WarningIcon height={16} width={16} />
        <span className="text-red-cardinal">{validationErrorMessage}</span>
      </div>
    );
  };

  const combinedDataTestId = `${dataTestIds.componentLibrary["Molecules.RichTextArea.richTextArea"]}${dataTestId}`;

  return (
    <div className={wrapperClassNames} data-testid={combinedDataTestId}>
      <InputLabel label={label} annex={annex} />
      <div
        ref={editorHostRef}
        className={cx("quill", customClassNameForQuill)}
      />
      {(maxLength || isInvalid) && (
        <span
          className={`${customClassNameForCounter} rich-text__word-count-below`}
        >
          {isInvalid ? getValidationError() : `${charCount}/${maxLength}`}
        </span>
      )}
    </div>
  );
};

export default RichTextArea;
